import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { jwtDecode } from "jwt-decode";
import {
  Alert,
  Box,
  Button,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveOrg, useActiveShow } from "common-fe";
import useReviewQueue from "../hooks/useReviewQueue";
import AttachRideDialog from "../components/AttachRideDialog";
import AttachOrphanSheetDialog from "../components/AttachOrphanSheetDialog";
import RideLink, { formatRideLabel } from "../components/RideLink";
import ClassLink from "../components/ClassLink";
import FvdPushFailureCauseChip from "../components/FvdPushFailureCauseChip";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { classNumberLabel } from "../util/classDisplay";
import useVenueDateTime from "../hooks/useVenueDateTime";
import { useScoreApi, unwrap } from "../api/scoreApi";
import QuarantinePayloadDetail from "../components/QuarantinePayloadDetail";
import SheetVersionsDialog from "../components/SheetVersionsDialog";
import ReviewItemSummary from "../components/ReviewItemSummary";
import TechnicalDetails from "../components/TechnicalDetails";
import ReviewDoneTab from "../components/ReviewDoneTab";
import ImportSheetsButton from "../components/ImportSheetsButton";
import {
  hasImportTabletSheetsPermission,
  hasPickSheetVersionPermission,
  hasResolveStaleWriteConflictPermission,
  hasApplyQuarantinedPayloadPermission,
} from "../permissions";

// #3009 - a conflicted field's value for display: `null`/`undefined` (the
// field was never set) renders as a plain placeholder rather than a blank
// cell or the literal string "null". #3479: a nested/array value is never
// dumped as JSON on screen, it reads "(several values)".
function formatConflictValue(value) {
  if (value === null || value === undefined) return "(none)";
  if (typeof value === "object") return "(several values)";
  return String(value);
}

// #3478 - the two kinds of item whose fix is picking one whole sheet version
// (GLOSSARY "Sheet version"): a held copy (stale_write) and a second sheet at a
// seat (duplicate_score_sheet). Both open the Sheet versions screen; neither
// reads item.options (the old Keep current / Apply attempted and the broken
// Compare button), and neither goes through the generic resolve route.
const SHEET_VERSION_TYPES = new Set(["stale_write", "duplicate_score_sheet"]);

// #3009 - resolveStaleWriteConflict can fail with five specific codes
// (concurrent_modification, sheet_completed, validation_error, and, judge
// round 2 D2, marks_confirmed_frozen / elimination_change_needs_sheet_edit
// on apply_attempted) on top of the generic no_conflict/invalid_action
// shapes every other resolve already covers. Each gets its own message so
// score_admin knows WHY the resolve was refused and what to do instead
// (usually: Keep current value here, fix it on the sheet itself), not just
// that it was. A plain lookup table of static key strings
// (never `t(\`...${code}\`)`) -- i18n keys must stay static so the
// extraction tooling can see every string (memory: feedback_no_dynamic_i18n_keys.md).
const STALE_WRITE_RESOLVE_ERROR_I18N_KEY = {
  concurrent_modification: "reviewQueue.other.staleWriteConflict.resolveErrors.concurrentModification",
  sheet_completed: "reviewQueue.other.staleWriteConflict.resolveErrors.sheetCompleted",
  validation_error: "reviewQueue.other.staleWriteConflict.resolveErrors.validationError",
  marks_confirmed_frozen: "reviewQueue.other.staleWriteConflict.resolveErrors.marksConfirmedFrozen",
  elimination_change_needs_sheet_edit:
    "reviewQueue.other.staleWriteConflict.resolveErrors.eliminationChangeNeedsSheetEdit",
  // #3140 - the Test's def was corrected after the sheet was confirmed.
  def_changed_since_confirm: "reviewQueue.other.staleWriteConflict.resolveErrors.defChangedSinceConfirm",
  // #3482 - applying an archived held copy.
  archive_entry_not_found: "reviewQueue.other.staleWriteConflict.resolveErrors.archiveEntryNotFound",
  archive_entry_not_applicable: "reviewQueue.other.staleWriteConflict.resolveErrors.archiveEntryNotApplicable",
  already_resolved: "reviewQueue.other.staleWriteConflict.resolveErrors.alreadyApplied",
};

// #3482 - why a held copy was archived, shown beside the live held copy.
const ARCHIVED_REASON_I18N_KEY = {
  displaced_by_other_writer: "reviewQueue.other.staleWriteConflict.archive.reasons.displacedByOtherWriter",
  older_than_held: "reviewQueue.other.staleWriteConflict.archive.reasons.olderThanHeld",
  overridden_by_same_writer: "reviewQueue.other.staleWriteConflict.archive.reasons.overriddenBySameWriter",
  resolved: "reviewQueue.other.staleWriteConflict.archive.reasons.resolved",
  finalize_refused: "reviewQueue.other.staleWriteConflict.archive.reasons.finalizeRefused",
};

function staleWriteResolveErrorKey(code) {
  return STALE_WRITE_RESOLVE_ERROR_I18N_KEY[code] || "reviewQueue.other.staleWriteConflict.resolveErrors.default";
}

// #3479 - what a failed Clear says, per kind of item. STATIC i18n keys (never
// built from the type); a kind with no entry gets the plain "other" text.
const RESOLVE_ERROR_KEY = {
  quarantined_payload: "reviewQueue.resolveErrors.quarantined_payload",
  ride_review: "reviewQueue.resolveErrors.ride_review",
  fvd_orphaned_scored_ride: "reviewQueue.resolveErrors.fvd_orphaned_scored_ride",
  fvd_test_change: "reviewQueue.resolveErrors.fvd_test_change",
  totals_disagreement: "reviewQueue.resolveErrors.totals_disagreement",
  orphan_sheet: "reviewQueue.resolveErrors.orphan_sheet",
  other: "reviewQueue.resolveErrors.other",
};

// #3185 - the Issue type column and filter. Keys are score-common's
// REVIEW_ISSUE_TYPES values (score-api stamps one on every item). A plain
// lookup table of STATIC i18n keys, never `t(\`...${issueType}\`)` (memory:
// feedback_no_dynamic_i18n_keys.md). Array order is the filter's option order.
const ISSUE_TYPE_I18N_KEY = [
  ["manual_ride", "reviewQueue.issueTypes.manual_ride"],
  ["fvd_writeback_failure", "reviewQueue.issueTypes.fvd_writeback_failure"],
  ["quarantined_payload", "reviewQueue.issueTypes.quarantined_payload"],
  ["unsigned_sheet", "reviewQueue.issueTypes.unsigned_sheet"],
  ["contender_pick", "reviewQueue.issueTypes.contender_pick"],
  ["station_reconcile", "reviewQueue.issueTypes.station_reconcile"],
  ["stale_write_conflict", "reviewQueue.issueTypes.stale_write_conflict"],
  ["scribe_review_request", "reviewQueue.issueTypes.scribe_review_request"],
  ["totals_disagreement", "reviewQueue.issueTypes.totals_disagreement"],
  ["fvd_orphan", "reviewQueue.issueTypes.fvd_orphan"],
  ["fvd_test_change", "reviewQueue.issueTypes.fvd_test_change"],
  ["orphan_sheet", "reviewQueue.issueTypes.orphan_sheet"],
  ["other", "reviewQueue.issueTypes.other"],
];
const ISSUE_TYPE_KEY_BY_VALUE = Object.fromEntries(ISSUE_TYPE_I18N_KEY);

// An item from an older server (no issueType) or a value this build has never
// heard of is shown as "Other" rather than blank.
function issueTypeOf(item) {
  return ISSUE_TYPE_KEY_BY_VALUE[item.issueType] ? item.issueType : "other";
}

// The em dash a cell shows when the item kind has nothing for that column.
const EMPTY_CELL = "—";

// #2324 - a stranded score is read as a percentage to 3 decimals (the unit and
// precision the judge signed, e.g. 61.923), "-" when no sheet carries one.
function formatPercentage(value) {
  return typeof value === "number" && Number.isFinite(value) ? `${value.toFixed(3)}%` : EMPTY_CELL;
}

// #2813 (scribe-resilience workstream, gap G6) - admin half of the generalised
// review queue: subject/reason/decision/options/provenance/resolution, not a
// bare ride row. #3185 - ONE table for every kind of item (manual rides, FVD
// write-back failures, quarantined payloads, unsigned sheets, ...), each row
// labelled by its Issue type. Every row's Actions column is DERIVED from
// item.options rather than a hardcoded button per kind: "attach" opens the
// existing AttachRideDialog (it needs an operator-chosen target + the
// test-mismatch guard), any other action calls the generic resolve endpoint
// directly. Nothing below branches on `reason` for behaviour; the per-kind
// branching is display only (the Detail cell).
// Subject types whose server payload has a show / class field at all (#3411,
// #3413, #3185). For these a missing name reads "Unknown show" / "Unknown
// class" whether the id is null or just unresolved; the dash is kept for a kind
// with no such field (a quarantined payload and a station reconcile carry no
// class). `ride` is handled by its own class branch below.
const SHOW_FIELD_TYPES = new Set([
  "ride",
  "ride_review",
  "fvd_push_failed",
  "fvd_orphaned_scored_ride",
  "fvd_test_change",
  "confirmed_unsigned_sheet",
  "duplicate_score_sheet",
  "stale_write",
  "totals_disagreement",
  "quarantined_payload",
  "station_ship_disagreement",
  "orphan_sheet",
]);
const CLASS_FIELD_TYPES = new Set([
  "ride_review",
  "fvd_push_failed",
  "fvd_orphaned_scored_ride",
  "fvd_test_change",
  "confirmed_unsigned_sheet",
  "duplicate_score_sheet",
  "stale_write",
  "totals_disagreement",
  "orphan_sheet",
]);

export default function ReviewQueue() {
  const formatVenueLocalDateTime = useVenueDateTime();
  const { t } = useTranslation("scoreadmin");
  const navigate = useNavigate();
  // #3570 -- ?item=<id> is the Pre-show dashboard's Fix link: that one held item
  // is highlighted and scrolled into view (ADR 0004, a link to the exact thing).
  const [searchParams] = useSearchParams();
  const focusItemId = searchParams.get("item");
  useDocumentTitle(t("title.reviewQueue"));
  // #3413 / #3511 - always scoped to the selected show, on TO DO and DONE.
  // There is no "include other shows" widening: to see another show's items,
  // switch shows in the ShowSwitcher and the queue refetches for that show.
  const { activeShowId, loading: loadingShow } = useActiveShow();
  const scopedShowId = activeShowId || undefined;
  const { items, loading, error, refetch, resolveItem } = useReviewQueue({
    showId: scopedShowId,
    ready: !loadingShow,
  });
  const scoreApi = useScoreApi();
  const [selectedRide, setSelectedRide] = useState(null);
  // #3516 - the "score on a missing ride" item whose Put-on-its-real-ride
  // dialog is open, and the plain-words note shown after one landed.
  const [orphanItem, setOrphanItem] = useState(null);
  const [orphanNotice, setOrphanNotice] = useState(null);
  const [search, setSearch] = useState("");
  const [issueFilter, setIssueFilter] = useState("");
  const [resolveError, setResolveError] = useState(null);
  // #3478 - the item whose Sheet versions screen is open, and the plain-words
  // note shown after a pick landed.
  const [versionsItem, setVersionsItem] = useState(null);
  const [pickedNotice, setPickedNotice] = useState(null);
  // #3534 - shown after a Switch that kept a test staff had set by hand.
  const [testChangeNotice, setTestChangeNotice] = useState(null);
  // #3480 - "todo" (the queue) or "done" (what was resolved, with Undo).
  const [tab, setTab] = useState("todo");
  useEffect(() => {
    if (!focusItemId || tab !== "todo") return;
    document.getElementById(`review-item-${focusItemId}`)?.scrollIntoView?.({ block: "center" });
  }, [focusItemId, tab, items]);

  // #3478 - does this user hold pick_sheet_version for the active org? Same
  // literal-roles-claim predicate RideEditor.jsx's canSwitchAuthoritative
  // already uses (permissions.js's own header explains why: no common-fe
  // usePermission(), account-api emits no `permissions` claim). Drives whether
  // the Sheet versions screen offers its pick buttons at all - a scribe-only
  // token can look at the versions but never pick one.
  const auth = useAuth();
  const { activeOrgId, isSuper } = useActiveOrg();
  const rolesForActiveOrg = useMemo(() => {
    const accessToken = auth.user?.access_token;
    if (!accessToken || !activeOrgId) return undefined;
    try {
      const claims = jwtDecode(accessToken);
      const raw = claims.roles;
      const map = typeof raw === "string" ? JSON.parse(raw) : raw || {};
      return map[activeOrgId];
    } catch {
      return undefined;
    }
  }, [auth.user?.access_token, activeOrgId]);
  // #3414: a super admin passes with no roles for the active org.
  const canPickSheetVersion =
    isSuper === true || (!!rolesForActiveOrg && hasPickSheetVersionPermission(rolesForActiveOrg, isSuper));
  // #3482 - applying an archived held copy keeps the resolve_stale_write_conflict gate.
  const canResolveStaleWriteConflict =
    isSuper === true || (!!rolesForActiveOrg && hasResolveStaleWriteConflictPermission(rolesForActiveOrg, isSuper));
  // #3356 - Apply on a quarantined row replays a held write, so it shows only
  // to roles holding apply_quarantined_payload (score_admin). The server gates
  // the route the same way; this only decides whether the button exists.
  const canApplyQuarantinedPayload =
    isSuper === true || (!!rolesForActiveOrg && hasApplyQuarantinedPayloadPermission(rolesForActiveOrg, isSuper));

  // #3575 - Import sheets (a tablet's saved file) shows only to import_tablet_sheets.
  const canImportTabletSheets =
    isSuper === true || (!!rolesForActiveOrg && hasImportTabletSheetsPermission(rolesForActiveOrg, isSuper));

  // #3482 - apply ONE archived held copy (a copy a later save displaced).
  // Same permission and server guards as Apply attempted value; the queue is
  // refetched on success AND on a refusal so the row shows the real state.
  const handleApplyArchived = async (item, copy) => {
    setResolveError(null);
    try {
      unwrap(
        await scoreApi.scoreSheets.applyArchivedHeldCopy(item.subject.id, {
          index: copy.index,
          archivedAt: copy.archivedAt,
        }),
      );
    } catch (err) {
      setResolveError({ i18nKey: staleWriteResolveErrorKey(err?.response?.data?.code) });
    }
    await refetch();
  };

  // A single generic dispatch for every row: "attach" always means "needs an
  // operator-chosen target", regardless of reason, so it opens the picker;
  // every other action code resolves directly. This is the ONLY place that
  // branches on an action name.
  const handleOption = async (item, option) => {
    if (option.action === "attach") {
      setSelectedRide(item.subject);
      return;
    }
    // #2927 — "sign" deep-links into the existing Complete & Sign flow
    // (RideEditor, /rides/:rideId) rather than resolving generically: this
    // subject type has no generic resolution (lib/review-queue.js's
    // confirmed_unsigned_sheet catalog entry), it just drops out of the next
    // GET /review-queue once the real completeScoreSheet endpoint stamps
    // completedAt. AC2 (judge round 1) — the ride can carry 2+ sheets (one
    // per judge), and RideEditor keeps its own activeSheetId independent of
    // the URL, so a bare /rides/:rideId lands on whichever sheet loads
    // first, not necessarily the unsigned one this item is about. The
    // `?sheet=` query param carries item.subject.id (the sheet id) so
    // RideEditor can open the right tab directly.
    // #3516 - a score saved for a ride that does not exist: staff pick its real
    // ride in a dialog (the server proposes one). Not the manual-ride attach.
    if (option.action === "attach_sheet") {
      setOrphanNotice(null);
      setOrphanItem(item);
      return;
    }
    if (option.action === "sign") {
      navigate(`/rides/${item.subject.rideId}?sheet=${item.subject.id}`);
      return;
    }
    // #3268 - a station ship-log disagreement is reviewed here, in place. Its
    // row lives in the station-reconcile table, so it goes through the
    // station's own markStationReconcileReviewed route (not the generic
    // /review-queue/:itemId/resolve), sending the reconciledAt this row was
    // loaded with as the optimistic lock: a fresh reconcile since then 409s,
    // we say so and refetch so the row shows the current result.
    if (item.subject?.type === "station_ship_disagreement") {
      setResolveError(null);
      const { showId, deviceId, scribeSub, reconciledAt } = item.subject;
      try {
        unwrap(
          await scoreApi.stationSnapshot.markStationReconcileReviewed(showId, deviceId, scribeSub, { reconciledAt }),
        );
        await refetch();
      } catch (err) {
        const isConflict = err?.response?.status === 409;
        setResolveError({
          i18nKey: isConflict ? "reviewQueue.stationReconcile.staleReconcile" : "reviewQueue.stationReconcile.markReviewedError",
        });
        if (isConflict) await refetch();
      }
      return;
    }
    setResolveError(null);
    setTestChangeNotice(null);
    try {
      const result = await resolveItem(item.id, option.action);
      // #3534 - staff set this ride's test by hand after FVD changed it, so
      // Switch kept the staff-set test. Say so, the test did not move to FVD's.
      if (item.subject?.type === "fvd_test_change" && result?.overrideWon === true) {
        setTestChangeNotice({ key: "reviewQueue.other.fvdTestChange.overrideWon" });
      }
    } catch (err) {
      // #3534 - a test change: the class already has placings (403), or
      // someone changed this item while it was open (409). Plain words, and a
      // 409 reloads the list so the row shows what is true now.
      if (item.subject?.type === "fvd_test_change") {
        const status = err?.response?.status;
        if (status === 403) {
          setResolveError({ i18nKey: "reviewQueue.other.fvdTestChange.errors.classPlaced" });
          return;
        }
        if (status === 409) {
          setResolveError({ i18nKey: "reviewQueue.other.fvdTestChange.errors.changed" });
          await refetch();
          return;
        }
      }
      // #3479 - say what went wrong for THIS kind of item.
      // #3516 - a sheet that already left the missing-ride state (someone
      // attached or set it aside first) says so, and the list refreshes.
      if (item.subject?.type === "orphan_sheet" && err?.response?.data?.code === "sheet_not_orphan") {
        setResolveError({ i18nKey: "reviewQueue.orphanSheet.errors.sheetNotOrphan" });
        await refetch();
        return;
      }
      setResolveError({ i18nKey: RESOLVE_ERROR_KEY[item.subject?.type] ?? RESOLVE_ERROR_KEY.other });
    }
  };

  // The issue types that actually have a row right now, in the fixed list's
  // order: the filter never offers a choice that would empty the table.
  const presentIssueTypes = useMemo(() => {
    const present = new Set(items.map(issueTypeOf));
    return ISSUE_TYPE_I18N_KEY.filter(([value]) => present.has(value));
  }, [items]);

  // #869 — search over CNO/rider/horse/class, matching the ClassManager /
  // PeopleManager / HorseManager filtered-list pattern. Class text prefers
  // the resolved classNumberLabel (#864's join) so a search matches the
  // visible label, falling back to the class name an FVD item carries, then
  // the raw classId when the lookup missed. #3185 adds the show name.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (issueFilter && issueTypeOf(item) !== issueFilter) return false;
      if (!q) return true;
      const s = item.subject ?? {};
      return [
        s.competitorNumber,
        s.riderName,
        s.horseName,
        s.showName,
        s.class ? classNumberLabel(s.class) : (s.className ?? s.classId),
      ].some((v) => v != null && String(v).toLowerCase().includes(q));
    });
  }, [items, search, issueFilter]);

  // The Show cell. The server names every item that carries a showId, of any
  // kind (#3185, #3413 sheets via their ride, #3411 the rest). A kind that
  // carries a show reads "Unknown show" when the name is missing, null showId
  // or unresolved alike (#3413 AC3), never the raw id. Only a kind with no
  // show field at all keeps the dash.
  const renderShow = (item) => {
    const s = item.subject ?? {};
    if (s.showName) return s.showName;
    return SHOW_FIELD_TYPES.has(s.type) || s.showId ? t("reviewQueue.fvdFailures.showUnknown") : EMPTY_CELL;
  };

  // The Ride cell. Kinds that name a ride link into its editor (#860); a sheet
  // kind (unsigned / stale write / totals) names the ride's competitor as plain
  // text, same as the old "Other review items" rows did; a quarantined payload
  // names its ride when the held save pointed at one (#3479).
  const renderRide = (item) => {
    const s = item.subject ?? {};
    switch (s.type) {
      case "ride":
        return <RideLink ride={s} rideId={s.id ?? s.rideId} />;
      case "fvd_push_failed":
      case "fvd_orphaned_scored_ride":
      case "fvd_test_change":
      case "ride_review":
        return <RideLink ride={s} rideId={s.rideId} />;
      case "quarantined_payload":
        // #3479 - a held save names the ride it was aimed at when that ride
        // could be found; otherwise (no ride id, or a ride that is gone) no ride.
        if (s.rideId && (s.riderName || s.competitorNumber != null)) {
          return <RideLink ride={s} rideId={s.rideId} />;
        }
        // #3551 - a held manual-ride create has no ride yet; its entry number
        // and names still say who it was for.
        return formatRideLabel(s, t("rideEditor.onHorse")) ?? EMPTY_CELL;
      case "confirmed_unsigned_sheet":
      case "stale_write":
      case "duplicate_score_sheet":
      case "totals_disagreement":
      case "orphan_sheet":
        return formatRideLabel(s, t("rideEditor.onHorse")) ?? EMPTY_CELL;
      default:
        return EMPTY_CELL;
    }
  };

  // The Class cell. A manual ride carries the resolved class object
  // (useReviewQueue's batch lookup, #864); an FVD item carries classId plus
  // className straight from the server (#3185, no browser lookups).
  const renderClass = (item) => {
    const s = item.subject ?? {};
    if (s.type === "ride") {
      // #865 — surface a failed lookup as a placeholder, not the raw id; the
      // id still reaches support via the tooltip.
      const classLookupMissed = !!s.classId && !s.class;
      return (
        <Tooltip title={classLookupMissed ? s.classId : ""}>
          <span>
            <ClassLink classObj={s.class} classId={s.classId} fallback={t("common.unknownClass")} />
          </span>
        </Tooltip>
      );
    }
    // Every other kind with a class field (FVD #3185, sheets #3413, scribe
    // review #3411) gets its class name from the server, linked like the rest;
    // a missing name reads "Unknown class" (null id or unresolved), never the
    // raw id. A kind with no class field keeps the dash.
    if (CLASS_FIELD_TYPES.has(s.type) || s.classId) {
      return (
        <ClassLink classId={s.classId} fallback={t("common.unknownClass")}>
          {s.className || null}
        </ClassLink>
      );
    }
    return EMPTY_CELL;
  };

  // #3316 / #3508 - "Station pine-frog (\u20267f3a), scribe Sam Scribe". The name
  // was given once at setup; the last 4 characters of the id tell two same-name
  // stations apart. A station with no name yet reads as "not yet named". An
  // unresolved scribe is simply left off.
  const stationLine = (s) => {
    const suffix = String(s.deviceId ?? "").slice(-4) || EMPTY_CELL;
    const station = s.stationName
      ? t("reviewQueue.stationReconcile.station", { name: s.stationName, suffix })
      : t("reviewQueue.stationReconcile.stationUnnamed", { suffix });
    return s.scribeName ? t("reviewQueue.stationReconcile.stationScribe", { station, scribe: s.scribeName }) : station;
  };

  // The ride a missing sheet belongs to, linked into its editor so the
  // scorer can re-score it. A ride the server no longer has is said so
  // plainly instead of linking to a dead page.
  const stationRideCell = (d) => {
    const label = formatRideLabel(d, t("rideEditor.onHorse"));
    if (label && d.rideId) return <RideLink ride={d} rideId={d.rideId} />;
    if (d.rideId) return t("reviewQueue.stationReconcile.rideGone");
    return EMPTY_CELL;
  };

  const stationJudgeCell = (d) => {
    if (!d.judgeId) return EMPTY_CELL;
    return d.judgeName
      ? t("reviewQueue.stationReconcile.judgeNamed", { position: d.judgeId, name: d.judgeName })
      : t("reviewQueue.stationReconcile.judgePosition", { position: d.judgeId });
  };

  // The Detail cell: type-specific content, then the item's own decision text
  // (the guidance score-api's REASON_CATALOG writes for that reason).
  const renderDetail = (item) => {
    const s = item.subject ?? {};
    switch (s.type) {
      case "fvd_push_failed": {
        const pushedAt = s.fvdPushedAt ? formatVenueLocalDateTime(s.fvdPushedAt) : null;
        return (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Tooltip title={pushedAt ? t("reviewQueue.fvdFailures.pushedAtTooltip", { when: pushedAt }) : ""}>
                <span>
                  <FvdPushFailureCauseChip cause={s.cause} />
                </span>
              </Tooltip>
              <Typography variant="body2" color="text.primary" data-testid="fvd-push-failure-score">
                {t("reviewQueue.other.fvdOrphan.score", { value: formatPercentage(s.percentage) })}
              </Typography>
              <Typography variant="body2" color="text.secondary" data-testid="fvd-push-failure-attempts">
                {t("reviewQueue.detail.attempts", { attempts: s.attempts ?? t("reviewQueue.fvdFailures.attemptsUnknown") })}
              </Typography>
            </Box>
            {/* #3479 - the raw error text is technical: closed by default. */}
            <TechnicalDetails testId="fvd-push-failure-technical">
              <Typography variant="body2" color="text.secondary" data-testid="fvd-push-failure-detail">
                {s.rawError || t("reviewQueue.fvdFailures.noDetail")}
              </Typography>
            </TechnicalDetails>
          </>
        );
      }
      case "quarantined_payload":
        // #3356 - what was held, why it was refused, the held entry on demand,
        // and (admins) Apply.
        return <QuarantinePayloadDetail item={item} canApply={canApplyQuarantinedPayload} onApplied={refetch} />;
      case "fvd_orphaned_scored_ride":
        // #2324 - a SCORED ride whose FVD source row was deleted: the stranded
        // score so the office can act without a DynamoDB read.
        return (
          <Typography variant="body2" color="text.primary" data-testid="fvd-orphan-percentage">
            {t("reviewQueue.other.fvdOrphan.score", { value: formatPercentage(s.percentage) })}
          </Typography>
        );
      case "fvd_test_change":
        // #3534 - FVD changed the rider's test after scoring started: what it
        // was scored on and what FVD says now. The "FVD changed this rider's test
        // after scoring started" sentence is the item's own decision text above;
        // Switch / Keep are the row's buttons.
        return (
          <>
            <Typography variant="body2" color="text.secondary" data-testid="fvd-test-change-from-to">
              {t("reviewQueue.other.fvdTestChange.fromTo", {
                from: s.fromTestName || t("reviewQueue.other.fvdTestChange.unknownFrom"),
                to: s.toTestName || t("reviewQueue.other.fvdTestChange.unknownTo"),
              })}
            </Typography>
          </>
        );
      case "ride_review":
        // #3088 - a ride the scribe sent to review with REVIEW: the scribe's
        // own note.
        return (
          <Typography variant="body2" color="text.secondary" data-testid="scribe-review-comment">
            {s.comment
              ? t("reviewQueue.other.scribeReview.comment", { comment: s.comment })
              : t("reviewQueue.other.scribeReview.noComment")}
          </Typography>
        );
      case "orphan_sheet":
        // #3516 - a score saved for a ride that does not exist: whose score it
        // is, and the ride it most likely belongs to (the server's proposal).
        return (
          <>
            <Typography variant="body2" color="text.secondary">
              {t("reviewQueue.other.judgeLabel", { name: s.judgeName || t("common.unknownJudge") })}
            </Typography>
            <Typography variant="body2" color="text.primary" data-testid="orphan-sheet-score-line">
              {s.totalPoints != null
                ? t("reviewQueue.orphanSheet.rowScoreWithPoints", { value: formatPercentage(s.percentage), points: s.totalPoints })
                : t("reviewQueue.other.fvdOrphan.score", { value: formatPercentage(s.percentage) })}
            </Typography>
            <Typography variant="body2" color="text.secondary" data-testid="orphan-sheet-proposal-line">
              {s.proposedRide
                ? t("reviewQueue.orphanSheet.rowProposed", {
                    ride: formatRideLabel(s.proposedRide, t("rideEditor.onHorse")) ?? EMPTY_CELL,
                  })
                : t("reviewQueue.orphanSheet.rowNoProposal")}
            </Typography>
          </>
        );
      case "duplicate_score_sheet":
        // #3478 - a second sheet at this judge's seat: whose, nothing more
        // (the Sheet versions screen shows the sheets themselves).
        return (
          <Typography variant="body2" color="text.secondary">
            {t("reviewQueue.other.judgeLabel", { name: s.judgeName || t("common.unknownJudge") })}
          </Typography>
        );
      case "confirmed_unsigned_sheet":
        // #2927 - identify the sheet without a click-through: the judge whose
        // sheet it is (the ride is in the Ride column).
        return (
          <Typography variant="body2" color="text.secondary">
            {t("reviewQueue.other.judgeLabel", { name: s.judgeName || t("common.unknownJudge") })}
          </Typography>
        );
      case "totals_disagreement":
        // #1421 - a sheet whose scribe-advised totals disagreed with the
        // server's recompute. The server's stored values stand; this shows
        // which fields differed and both values so the reviewer can chase the
        // cause (stale bundle / catalog).
        return (
          <>
            <Typography variant="body2" color="text.secondary">
              {t("reviewQueue.other.judgeLabel", { name: s.judgeName || t("common.unknownJudge") })}
            </Typography>
            <Table size="small" sx={{ mt: 1 }} data-testid="totals-disagreement-table">
              <TableHead>
                <TableRow>
                  <TableCell>{t("reviewQueue.other.totalsDisagreement.columns.field")}</TableCell>
                  <TableCell align="right">{t("reviewQueue.other.totalsDisagreement.columns.scribe")}</TableCell>
                  <TableCell align="right">{t("reviewQueue.other.totalsDisagreement.columns.server")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(s.differences || []).map((diff) => (
                  <TableRow key={diff.field} data-testid="totals-disagreement-row">
                    <TableCell>{diff.field}</TableCell>
                    <TableCell align="right">{formatConflictValue(diff.client)}</TableCell>
                    <TableCell align="right">{formatConflictValue(diff.server)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        );
      case "station_ship_disagreement":
        // #3268 - how many sheets the station says it shipped that the server
        // does not hold; the decision text below says what to do about it.
        // #3316 - and WHICH station, scribe and sheets: one table row per
        // missing sheet (ride, class, judge, when the server confirmed it).
        // Every field degrades to a placeholder, so a disagreement the server
        // could not tie to a ride (missing_on_server_unscoped) still renders.
        return (
          <>
            <Typography variant="body2" color="text.primary" data-testid="station-reconcile-station">
              {stationLine(s)}
            </Typography>
            <Typography variant="body2" color="text.secondary" data-testid="station-reconcile-count">
              {t("reviewQueue.stationReconcile.disagreements", { count: s.disagreementCount ?? 0 })}
            </Typography>
            {(s.disagreements || []).length > 0 && (
              <Table size="small" sx={{ mt: 1 }} data-testid="station-reconcile-table">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("reviewQueue.stationReconcile.columns.missing")}</TableCell>
                    <TableCell>{t("reviewQueue.stationReconcile.columns.ride")}</TableCell>
                    <TableCell>{t("reviewQueue.stationReconcile.columns.class")}</TableCell>
                    <TableCell>{t("reviewQueue.stationReconcile.columns.judge")}</TableCell>
                    <TableCell>{t("reviewQueue.stationReconcile.columns.ackedAt")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {s.disagreements.map((d, index) => (
                    <TableRow key={d.opId ?? d.entityId ?? index} data-testid="station-reconcile-sheet-row">
                      <TableCell>
                        {d.type === "createRide"
                          ? t("reviewQueue.stationReconcile.kind.ride")
                          : t("reviewQueue.stationReconcile.kind.sheet")}
                      </TableCell>
                      <TableCell>{stationRideCell(d)}</TableCell>
                      <TableCell>{d.className ?? EMPTY_CELL}</TableCell>
                      <TableCell>{stationJudgeCell(d)}</TableCell>
                      <TableCell>{d.acknowledgedAt ? formatVenueLocalDateTime(d.acknowledgedAt) : EMPTY_CELL}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        );
      case "stale_write":
        // #3478 - who the sheet is for, how many saves were held, and a held
        // Complete request. WHAT differs is on the Sheet versions screen the
        // row's button opens (the old per-field JSON rows are gone).
        return (
          <>
            <Typography variant="body2" color="text.secondary">
              {t("reviewQueue.other.judgeLabel", { name: s.judgeName || t("common.unknownJudge") })}
            </Typography>
            {/* #3444 - the scribe keeps re-saving a refused sheet and each save
                reports; the held copy is the NEWEST body, and this says how
                many reports were folded in and when the newest landed. */}
            {s.attemptCount > 1 && (
              <Typography variant="body2" color="text.secondary" data-testid="stale-write-attempt-count">
                {t("reviewQueue.other.staleWriteConflict.attempts", {
                  count: s.attemptCount,
                  time: (s.newestAttemptAt && formatVenueLocalDateTime(s.newestAttemptAt)) || "",
                })}
              </Typography>
            )}
            {/* #3478: the live held copy shows no raw field rows (Sheet versions screen); archived copies below keep theirs (#3482). */}
            {(s.archivedHeldCopies || []).length > 0 && (
              <Box sx={{ mt: 1 }} data-testid="stale-write-archive">
                <Typography variant="subtitle2" color="text.primary">
                  {t("reviewQueue.other.staleWriteConflict.archive.heading")}
                </Typography>
                {s.archivedHeldCopies.map((copy) => (
                  <Box
                    key={`${copy.index}-${copy.archivedAt}`}
                    sx={{ mt: 0.5, pl: 1, borderLeft: 2, borderColor: "divider" }}
                    data-testid="stale-write-archive-entry"
                  >
                    <Typography variant="body2" color="text.primary">
                      {t("reviewQueue.other.staleWriteConflict.archive.entryHeader", {
                        reason: t(ARCHIVED_REASON_I18N_KEY[copy.reason] || "reviewQueue.other.staleWriteConflict.archive.reasons.unknown"),
                        time: formatVenueLocalDateTime(copy.heldAt || copy.archivedAt) || "—",
                      })}
                    </Typography>
                    {(copy.fields || []).map((field) => (
                      <Typography key={field} variant="body2" color="text.secondary" data-testid="stale-write-archive-field">
                        {t("reviewQueue.other.staleWriteConflict.fieldRow", {
                          field,
                          current: formatConflictValue(copy.current?.[field]),
                          attempted: formatConflictValue(copy.attempted?.[field]),
                        })}
                      </Typography>
                    ))}
                    {copy.intentType && (
                      <Typography variant="body2" color="text.secondary">
                        {t("reviewQueue.other.staleWriteConflict.archive.intentOnly", {
                          action: t(
                            copy.intentType === "complete"
                              ? "reviewQueue.other.staleWriteConflict.heldIntentComplete"
                              : "reviewQueue.other.staleWriteConflict.heldIntentConfirm",
                          ),
                        })}
                      </Typography>
                    )}
                    {copy.hasSignature && (
                      <Typography variant="body2" color="text.secondary">
                        {t("reviewQueue.other.staleWriteConflict.archive.signatureNotApplied")}
                      </Typography>
                    )}
                    {copy.appliedAt && (
                      <Typography variant="body2" color="text.secondary" data-testid="stale-write-archive-applied">
                        {t("reviewQueue.other.staleWriteConflict.archive.applied", {
                          time: formatVenueLocalDateTime(copy.appliedAt) || "—",
                        })}
                      </Typography>
                    )}
                    {copy.canApply && canResolveStaleWriteConflict && (
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5, color: "text.primary", borderColor: "text.primary" }}
                        onClick={() => handleApplyArchived(item, copy)}
                      >
                        {t("reviewQueue.other.staleWriteConflict.archive.apply")}
                      </Button>
                    )}
                  </Box>
                ))}
              </Box>
            )}
            {s.requestedIntent && (
              <Alert severity="warning" sx={{ mt: 1 }} data-testid="stale-write-requested-intent">
                {t("reviewQueue.other.staleWriteConflict.heldIntent", {
                  action: t(
                    s.requestedIntent.type === "complete"
                      ? "reviewQueue.other.staleWriteConflict.heldIntentComplete"
                      : "reviewQueue.other.staleWriteConflict.heldIntentConfirm",
                  ),
                  time: formatVenueLocalDateTime(s.requestedIntent.requestedAt) || "—",
                })}
              </Alert>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("reviewQueue.title")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t("reviewQueue.subtitle")}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="todo" label={t("reviewQueue.tabs.todo")} data-testid="review-tab-todo" />
        <Tab value="done" label={t("reviewQueue.tabs.done")} data-testid="review-tab-done" />
        {canImportTabletSheets && (
          <Tab value="import" label={t("reviewQueue.tabs.import")} data-testid="review-tab-import" />
        )}
      </Tabs>

      {tab === "import" && canImportTabletSheets && (
        <ImportSheetsButton
          activeShowId={scopedShowId}
          onImported={refetch}
          onGoToTodo={() => setTab("todo")}
        />
      )}
      {tab === "done" && <ReviewDoneTab showId={scopedShowId} ready={!loadingShow} onUndone={refetch} />}

      {tab === "todo" && resolveError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="review-item-resolve-error">
          {t(resolveError.i18nKey)}
        </Alert>
      )}

      {tab === "todo" && orphanNotice && (
        <Alert severity="success" sx={{ mb: 2 }} data-testid="orphan-sheet-notice">
          {t(orphanNotice.key)}
        </Alert>
      )}

      {tab === "todo" && testChangeNotice && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="fvd-test-change-override-notice">
          {t(testChangeNotice.key)}
        </Alert>
      )}

      {tab === "todo" && pickedNotice && (
        <Alert severity="success" sx={{ mb: 2 }} data-testid="review-item-picked-notice">
          {t(pickedNotice.signatureCarried ? "sheetVersions.pickedSigned" : "sheetVersions.picked")}
        </Alert>
      )}

      {tab === "todo" && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("reviewQueue.loadError")}
        </Alert>
      )}

      {tab !== "todo" ? null : loading ? (
        <TableSkeleton rows={4} columns={6} />
      ) : items.length === 0 ? (
        <Typography color="text.secondary">
          {t(scopedShowId ? "reviewQueue.emptyForShow" : "reviewQueue.empty")}
        </Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              sx={{ maxWidth: 360 }}
              placeholder={t("reviewQueue.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <TextField
              select
              size="small"
              sx={{ minWidth: 240 }}
              label={t("reviewQueue.issueTypeFilter.label")}
              value={issueFilter}
              onChange={(e) => setIssueFilter(e.target.value)}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">{t("reviewQueue.issueTypeFilter.all")}</option>
              {presentIssueTypes.map(([value, key]) => (
                <option key={value} value={value}>
                  {t(key)}
                </option>
              ))}
            </TextField>
          </Box>

          {filtered.length === 0 ? (
            <Typography color="text.secondary">{t("reviewQueue.noResults")}</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("reviewQueue.columns.issueType")}</TableCell>
                    <TableCell>{t("reviewQueue.columns.show")}</TableCell>
                    <TableCell>{t("reviewQueue.columns.ride")}</TableCell>
                    <TableCell>{t("reviewQueue.columns.class")}</TableCell>
                    <TableCell>{t("reviewQueue.columns.detail")}</TableCell>
                    <TableCell align="right">{t("reviewQueue.columns.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((item) => {
                    const issueType = issueTypeOf(item);
                    const isSheetVersionItem = SHEET_VERSION_TYPES.has(item.subject?.type);
                    return (
                      <TableRow
                        key={item.id}
                        hover
                        id={`review-item-${item.id}`}
                        selected={focusItemId != null && String(item.id) === focusItemId}
                        data-testid="review-item-row"
                      >
                        <TableCell data-testid="review-item-issue-type">
                          {t(ISSUE_TYPE_KEY_BY_VALUE[issueType])}
                        </TableCell>
                        <TableCell data-testid="review-item-show">{renderShow(item)}</TableCell>
                        <TableCell data-testid="review-item-ride">{renderRide(item)}</TableCell>
                        <TableCell data-testid="review-item-class">{renderClass(item)}</TableCell>
                        <TableCell data-testid="review-item-subject">
                          <ReviewItemSummary
                            item={item}
                            fallback={
                              item.subject?.type === "stale_write"
                                ? t("sheetVersions.decisionHeld")
                                : item.subject?.type === "duplicate_score_sheet"
                                  ? t("sheetVersions.decisionContender")
                                  : item.decision
                            }
                          />
                          {renderDetail(item)}
                        </TableCell>
                        <TableCell align="right">
                          {/* #2813 AC3 - buttons derived from item.options,
                              never a hardcoded action per reason. #3478 -
                              except a sheet-version item (held copy or a
                              second sheet), whose one action is opening the
                              Sheet versions screen. */}
                          {isSheetVersionItem ? (
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ color: "text.primary", borderColor: "text.primary" }}
                              onClick={() => {
                                setPickedNotice(null);
                                setVersionsItem(item);
                              }}
                              data-testid="review-item-sheet-versions-button"
                            >
                              {t("sheetVersions.openButton")}
                            </Button>
                          ) : (
                            item.options.map((option, index) => (
                              <Button
                                key={option.action}
                                size="small"
                                variant="outlined"
                                sx={{ color: "text.primary", borderColor: "text.primary", ml: index > 0 ? 1 : 0 }}
                                onClick={() => handleOption(item, option)}
                                disabled={option.action === "attach_sheet" && !canPickSheetVersion}
                              >
                                {option.label}
                              </Button>
                            ))
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <SheetVersionsDialog
        open={!!versionsItem}
        item={versionsItem}
        canPick={canPickSheetVersion}
        onClose={async () => {
          // The dialog's empty and error states tell the admin to close it to
          // see the refreshed list, so closing always reloads the queue.
          setVersionsItem(null);
          await refetch();
        }}
        onPicked={async ({ signatureCarried }) => {
          setVersionsItem(null);
          setPickedNotice({ signatureCarried });
          await refetch();
        }}
      />

      <AttachOrphanSheetDialog
        open={!!orphanItem}
        item={orphanItem}
        onClose={() => setOrphanItem(null)}
        onAttached={async (result) => {
          setOrphanItem(null);
          // null = the sheet was no longer waiting; say so and refresh the list.
          if (!result) setResolveError({ i18nKey: "reviewQueue.orphanSheet.errors.sheetNotOrphan" });
          else {
            setResolveError(null);
            let key = "reviewQueue.orphanSheet.noticeAttached";
            if (result.completionError) key = "reviewQueue.orphanSheet.noticeCompletionFailed";
            else if (result.mode === "contender") key = "reviewQueue.orphanSheet.noticeContender";
            setOrphanNotice({ key });
          }
          await refetch();
        }}
      />

      <AttachRideDialog
        open={!!selectedRide}
        manualRide={selectedRide}
        onClose={() => setSelectedRide(null)}
        onResolved={refetch}
      />
    </Box>
  );
}
